import { openDB } from 'idb'
import type { IDBPDatabase } from 'idb'
import CryptoJS from 'crypto-js'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { useLogger } from '../../composables/useLogger'
import { Helper } from '../usermodel/Helper'

const logger = useLogger()

export type LatestAreaType = "User" | "Helper";

// Add DeviceLockStatus type
export type DeviceLockStatus = 'locked' | 'unlocked' | 'unknown';

export interface UserHelperState {
    userId: string;
    helperId: string | null;
    deviceLockStatus: DeviceLockStatus;
}

/**
 * UserCustomerStorage is used to store the userId in the IndexedDB, signalling that the current
 * user is a User, rather than a Helper user.
 */
export class UserCustomerStorage {
    private static instance: UserCustomerStorage;
    private db: IDBPDatabase | null = null;
    private dbName: string;
    private storeName: string;
    private dbVersion: number;
    private encryptionKey: string;
    private static readonly USER_HELPER_STATE_KEY = "uhstate";
    private static readonly USER_LATEST_AREA_KEY = "userLatestArea";
    private isMigrating: boolean;
    private initializationPromise: Promise<void>;

    private constructor() {
        this.dbName = "UserDb";
        this.storeName = "userData";
        this.dbVersion = 3;
        this.encryptionKey = "jhfsdfkjhf skdjhf skdfjh sfdkjhsd fskjhf skfds"; // Choose a strong, unique key; // Securely store your encryption key
        this.isMigrating = false;
        this.initializationPromise = this.initialize();
    }

    /**
     * Retrieves the singleton instance of UserCustomerStorage.
     */
    public static getInstance(): UserCustomerStorage {
        logger.debug("Auth: UserStorage: Getting instance");
        if (!UserCustomerStorage.instance) {
            logger.debug("Auth: UserStorage: Creating new instance");
            UserCustomerStorage.instance = new UserCustomerStorage();
        }
        logger.debug("Auth: UserStorage: Returning instance");
        return UserCustomerStorage.instance;
    }

    /**
     * Initializes the IndexedDB connection and handles migrations.
     */
    private async initialize(): Promise<void> {
        this.db = await openDB(this.dbName, this.dbVersion, {
            upgrade: async (db, oldVersion, newVersion, transaction) => {
                logger.debug("Auth: UserStorage: Upgrading database", { oldVersion, newVersion });

                if (oldVersion < 2) {
                    logger.debug("Auth: UserStorage: Upgrading to version 2");
                    if (db.objectStoreNames.contains("userData")) {
                        db.deleteObjectStore("userData");
                    }
                    db.createObjectStore("userData");
                }

                if (oldVersion < 3) {
                    logger.debug("Auth: UserStorage: Upgrading to version 3");
                    if (!db.objectStoreNames.contains("userData")) {
                        db.createObjectStore("userData");
                    }
                    // migrate non-blocking but after initialization
                    setTimeout(async () => {
                        try {
                            logger.debug("Auth: UserStorage: Migrating to v3, starting migration, waiting for initialization");
                            await this.migrateToV3();
                        } catch (error) {
                            logger.logErrorCaught("Auth: UserStorage: Migration failed", "UserCustomerStorage.ts", error);
                            
                        }
                    }, 0);
                }
            },
        });

        logger.debug("Auth: UserStorage: Database initialized");
    }

    private async migrateToV3(): Promise<void> {
        logger.debug("Auth: UserStorage: Migrating to v3, ensuring initialized");
        await this.ensureInitialized();
        logger.debug("Auth: UserStorage: Migrating to v3, got initialized");
        
        if (!this.db) throw new Error("Database not initialized");

        const encryptedUserId = await this.db.get(this.storeName, "customerId");
        logger.debug("Auth: UserStorage: Migrating to v3, got encryptedUserId", { encryptedUserId });
        
        if (encryptedUserId) {
            // this is a locked user from V2, we need to migrate it to V3
            this.isMigrating = true;
            await this.db.delete(this.storeName, "customerId");
            logger.debug("Auth: UserStorage: Old customerId deleted");

            try {
                const decryptedUserId = this.decrypt(encryptedUserId);
                logger.debug("Auth: UserStorage: Decrypted userId", { decryptedUserId });

                const mainHelper = await Helper.getMainHelperFromUser(decryptedUserId);
                if (mainHelper?.id) {
                    const uhstate: UserHelperState = {
                        userId: decryptedUserId,
                        helperId: mainHelper.id,
                        deviceLockStatus: 'locked' // Since this was a locked user in v2, set to 'locked'
                    };
                    const encryptedState = this.encrypt(JSON.stringify(uhstate));
                    await this.db.put(this.storeName, encryptedState, UserCustomerStorage.USER_HELPER_STATE_KEY);
                    logger.debug("Auth: UserStorage: Saved migrated uhstate", { uhstate : uhstate });
                    // since this was a user, we will set the latest area to User
                    logger.debug("Auth: UserStorage: Setting latest area to User, due to migration of a locked User, to land User there after migration");
                    await this.db.put(this.storeName, 'User', UserCustomerStorage.USER_LATEST_AREA_KEY);
                    logger.debug("Auth: UserStorage: Migration completed, setting isMigrating to false");
                    this.isMigrating = false;
                    logger.debug("Auth: UserStorage: Migration completed, finished setting isMigrating to false");
                } else {
                    throw new Error("Post-migration uhstate creation failed, mainHelper is null");
                }
            } catch (error) {
                logger.logErrorCaught("Auth: UserStorage: Migration error", "UserCustomerStorage.ts", error);
                await this.db.delete(this.storeName, UserCustomerStorage.USER_HELPER_STATE_KEY);
                throw error;
            } finally {
                logger.debug("Auth: UserStorage: Migration completed, setting isMigrating to false");
                this.isMigrating = false;
            }
        }
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.initializationPromise) {
            throw new Error("Database initialization not started");
        }
        await this.initializationPromise;
    }

    /**
     * Ensures that the database is initialized and not migrating before proceeding.
     */
    private async ensureReady(): Promise<void> {
        if (this.isMigrating) {
            logger.debug("Auth: UserStorage: Waiting for migration to complete");
            await new Promise<void>((resolve) => {
                const checkMigration = () => {
                    logger.debug("Auth: UserStorage: Checking if migration is complete");
                    if (!this.isMigrating) {
                        resolve();
                    } else {
                        setTimeout(checkMigration, 100);
                    }
                };
                checkMigration();
            });
            logger.debug("Auth: UserStorage: Migration completed");
        }

        if (!this.initializationPromise) {
            throw new Error("Database initialization not started");
        }
  
        return this.initializationPromise;
    }

    private encrypt(text: string): string {
        const encrypted = CryptoJS.AES.encrypt(text, this.encryptionKey).toString();
        return encrypted;
    }

    private decrypt(encryptedText: string): string {
        const bytes = CryptoJS.AES.decrypt(encryptedText, this.encryptionKey);
        return bytes.toString(CryptoJS.enc.Utf8);
    }

    /**
     * saveUserState saves the userId and optionally helperId to the IndexedDB,
     * signalling that the current user is a User, rather than a Helper user
     * and providing the userId for the User for getting content etc
     * @param userId The user ID to save
     * @param helperId Optional helper ID to save
     */
    public async saveUserState(uhstate: UserHelperState): Promise<void> {
        await this.ensureReady();
        if (!this.db) throw new Error("Database not initialized");

        logger.debug("Auth: UserStorage: Saving user state", { uhstate: uhstate });

        if (this.dbVersion === 2) {
            throw new Error("Auth: UserStorage: v2 format is deprecated, please migrate to v3");
        } else {
            const encryptedState = this.encrypt(JSON.stringify(uhstate));
            logger.debug("Auth: UserStorage: encryptedState", { encryptedState: encryptedState });
            await this.db.put(this.storeName, encryptedState, UserCustomerStorage.USER_HELPER_STATE_KEY);
            logger.debug("Auth: UserStorage: Saved user state in v3 format");
        }
    }

    public async getUserState(): Promise<UserHelperState | null> {
        await this.ensureReady();
        if (!this.db) throw new Error("Database not initialized");

        logger.debug("Auth: UserStorage: Getting user state");

        if (this.dbVersion === 2) {
            throw new Error("Auth: UserStorage: v2 format is deprecated, please migrate to v3");
        } else {
            const encryptedState = await this.db.get(this.storeName, UserCustomerStorage.USER_HELPER_STATE_KEY);

            if (!encryptedState) {
                logger.debug("Auth: UserStorage: No user state found in v3 format");
                return null;
            }

            try {
                const decryptedState = this.decrypt(encryptedState);
                const parsedState = JSON.parse(decryptedState);
                
                // Ensure the state has the deviceLockStatus field
                const state: UserHelperState = {
                    userId: parsedState.userId,
                    helperId: parsedState.helperId,
                    deviceLockStatus: parsedState.deviceLockStatus || 'unknown'
                };
                
                logger.debug("Auth: UserStorage: encryptedState", { encryptedState: encryptedState });
                logger.debug("Auth: UserStorage: Parsed and retrieved v3 user state", { state });
                return state;
            } catch (error) {
                logger.logErrorCaught("Auth: UserStorage: Error parsing stored state", "UserCustomerStorage.ts", error);
                return null;
            }
        }
    }

    public async clearUserState(): Promise<void> {
        await this.ensureReady();
        if (!this.db) throw new Error("Database not initialized");

        logger.debug("Auth: UserStorage: Clearing user state");

        if (this.dbVersion === 2) {
            await this.db.delete(this.storeName, "customerId");
            logger.debug("Auth: UserStorage: Cleared v2 user state");
        } else {
            await this.db.delete(this.storeName, UserCustomerStorage.USER_HELPER_STATE_KEY);
            logger.debug("Auth: UserStorage: Cleared v3 user state");
        }
    }

    public async getLatestArea(): Promise<LatestAreaType | null> {
        await this.ensureReady();
        if (!this.db) throw new Error("Database not initialized");

        logger.debug("Auth: UserStorage: Getting latest area");

        try {
            const latestArea = await this.db.get(this.storeName, UserCustomerStorage.USER_LATEST_AREA_KEY) as LatestAreaType | null;
            logger.debug("Auth: UserStorage: Retrieved latest area", { latestArea });
            return latestArea;
        } catch (error) {
            logger.logErrorCaught("Auth: UserStorage: Error getting latest area", "UserCustomerStorage.ts", error);
            await this.db.put(this.storeName, 'User', UserCustomerStorage.USER_LATEST_AREA_KEY);
            logger.debug("Auth: UserStorage: Stored default 'User' area");
            return 'User';
        }
    }

    public async saveLatestArea(area: LatestAreaType): Promise<void> {
        await this.ensureReady();
        if (!this.db) throw new Error("Database not initialized");

        logger.debug("Auth: UserStorage: Saving latest area", { area });
        await this.db.put(this.storeName, area, UserCustomerStorage.USER_LATEST_AREA_KEY);
        logger.debug("Auth: UserStorage: Latest area saved");
    }
} 